import { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { VideoPlayerState, VideoPlayerStateObject, ReactPlayerInstance } from '../types';
import { VideoPlayerContext } from '../components/VideoPlayerProvider/VideoPlayerProvider';
import { VideoItemReference } from 'types/sharedComponents';
import { useVideoInteractionTracking } from './useVideoInteractionTracking';
import { VIDEO_PLAYER_CONSTANTS } from '../constants';
import {
  getPlayerProvider,
  getEffectiveMuted,
  createReactPlayerConfig,
  cleanupYouTubeIframe,
  cleanupTikTokIframe,
  isYouTubeUrl,
  ensureYouTubeApi,
} from '../utils';

interface UseVideoPlayerStateProps {
  video: VideoItemReference;
  autoPlay: boolean;
  playInBackground: boolean;
  lazyLoad: boolean;
  onReady: (currentPlayerId: string) => void;
  onStart: (currentPlayerId: string) => void;
  onPlay: (currentPlayerId: string) => void;
  onPause: (currentPlayerId: string) => void;
  onEnded: (currentPlayerId: string) => void;
  // Additional props for optimizations
  testId: string;
  muted: boolean;
  loop: boolean;
  hideNativeControls: boolean;
  mergedClassNames: { wrapper: string; player: string };
}

export const useVideoPlayerState = ({
  video,
  autoPlay,
  playInBackground,
  lazyLoad,
  onReady,
  onStart,
  onPlay,
  onPause,
  onEnded,
  // Additional props for optimizations
  testId,
  muted,
  loop,
  hideNativeControls,
  mergedClassNames,
}: UseVideoPlayerStateProps) => {
  const { registerPlayer, unregisterPlayer, notifyPlayerStarted } = useContext(VideoPlayerContext);
  const [playerRef, setPlayerRefState] = useState<ReactPlayerInstance | null>(null);
  const playerInstanceRef = useRef<ReactPlayerInstance | null>(null);
  const [playerState, setPlayerState] = useState<VideoPlayerState>(VideoPlayerState.Init);
  const [hasStarted, setHasStarted] = useState(false);
  const [isInEndingProcess, setIsInEndingProcess] = useState(false);
  const [hasRequestedPlayerMount, setHasRequestedPlayerMount] = useState(false);
  const previousStateRef = useRef<VideoPlayerState | null>(null);
  const pendingPlayRef = useRef(false);

  // Get video URL and player ID - memoized to prevent unnecessary recalculations
  const videoUrl = useMemo(() => {
    // Try alternative videoUrl property first (for external URLs like YouTube)
    if (video?.videoUrl) {
      return video.videoUrl;
    }
    // Fall back to uploaded video asset
    if (!video?.video?.url) return null;
    return video.video.url;
  }, [video?.video?.url, video?.videoUrl]);

  // Pre-load the YouTube IFrame API with data-ot-ignore before the web
  // component tries to load it (OneTrust would otherwise block it).
  if (videoUrl && isYouTubeUrl(videoUrl)) {
    ensureYouTubeApi();
  }

  // Derive player ID from testId to ensure uniqueness
  const playerId = useMemo(() => testId, [testId]);

  // Get video interaction tracking
  const { handleVideoInteraction } = useVideoInteractionTracking();

  // Memoize event handlers to prevent unnecessary re-renders
  const eventHandlers = useMemo(
    () => ({
      onReady: () => onReady(playerId),
      onStart: () => onStart(playerId),
      onPlay: () => onPlay(playerId),
      onPause: () => onPause(playerId),
      onEnded: () => onEnded(playerId),
    }),
    [playerId, onReady, onStart, onPlay, onPause, onEnded],
  );

  // Create memoized play/pause methods for the provider
  const playerMethods = useMemo(
    () => ({
      play: () => {
        pendingPlayRef.current = true;
        setPlayerState(VideoPlayerState.Playing);
      },
      pause: () => {
        pendingPlayRef.current = false;
        setPlayerState(VideoPlayerState.Paused);
      },
    }),
    [],
  );

  // React Player event handlers - these only change state.
  // pendingPlayRef guards against YouTube/iframe initialization events
  // (ready, start, pause) overriding a user-initiated play request before
  // the embedded player has actually begun playback.
  const handleReady = useCallback(() => {
    setPlayerState(currentState => {
      if (pendingPlayRef.current || currentState === VideoPlayerState.Playing) {
        return currentState;
      }
      return VideoPlayerState.Init;
    });
  }, []);

  const handleStart = useCallback(() => {
    setPlayerState(currentState => {
      if (pendingPlayRef.current || currentState === VideoPlayerState.Playing) {
        return currentState;
      }
      return VideoPlayerState.Starting;
    });
    setHasStarted(true);
  }, []);

  const handlePlay = useCallback(() => {
    if (!isInEndingProcess) {
      pendingPlayRef.current = false;
      setPlayerState(VideoPlayerState.Playing);
    }
  }, [isInEndingProcess]);

  const handlePause = useCallback(() => {
    if (hasStarted && !isInEndingProcess && !pendingPlayRef.current) {
      setPlayerState(VideoPlayerState.Paused);
    }
  }, [hasStarted, isInEndingProcess]);

  const handleEnded = useCallback(() => {
    pendingPlayRef.current = false;
    setPlayerState(VideoPlayerState.Ended);
    setIsInEndingProcess(true);

    // Clear the ending process flag after a delay to allow legitimate user interactions.
    // This delay should be sufficient for YouTube's auto-reset behavior to complete.
    setTimeout(() => {
      setIsInEndingProcess(false);
    }, 1000); // 1 second delay
  }, []);

  // Memoize the player object to prevent unnecessary re-registrations
  const playerObject = useMemo(
    () => ({
      id: playerId,
      playInBackground,
      pause: playerMethods.pause,
      playerRef,
      playerState,
    }),
    [playerId, playInBackground, playerMethods.pause, playerRef, playerState],
  );

  // Register/unregister player with context
  useEffect(() => {
    if (playerRef) {
      registerPlayer(playerObject);

      return () => {
        unregisterPlayer(playerId);
      };
    }
  }, [playerObject, registerPlayer, unregisterPlayer, playerId, playerRef]);

  // Cleanup effect for YouTube and TikTok iframes to prevent orphaned iframe errors
  useEffect(() => {
    return () => {
      // Cleanup function that runs when component unmounts
      if (playerRef) {
        cleanupYouTubeIframe(playerRef);
        cleanupTikTokIframe(playerRef);
      }
    };
  }, [playerRef]);

  // Monitor state changes and call appropriate event handlers
  useEffect(() => {
    const previousState = previousStateRef.current;
    previousStateRef.current = playerState;

    // Only trigger events on state transitions, not on initial render
    if (previousState === null) {
      // First render - only log Ready event
      eventHandlers.onReady();
      handleVideoInteraction(playerRef, VideoPlayerState.Init, video);
      return;
    }

    switch (playerState) {
      case VideoPlayerState.Init:
        // Only call onReady if we're transitioning TO Init from a different state
        if (previousState !== VideoPlayerState.Init) {
          eventHandlers.onReady();
          handleVideoInteraction(playerRef, VideoPlayerState.Init, video);
        }
        break;
      case VideoPlayerState.Starting:
        eventHandlers.onStart();
        break;
      case VideoPlayerState.Playing:
        // Only notify provider if we're transitioning TO Playing from a different state
        if (previousState !== VideoPlayerState.Playing) {
          // Fire onStart when transitioning directly from Init to Playing.
          // React batches the Starting → Playing state updates from the
          // combined play handler, so the intermediate Starting state is
          // never observed by this effect.
          if (previousState === VideoPlayerState.Init) {
            eventHandlers.onStart();
          }
          notifyPlayerStarted(playerId, playInBackground);
          eventHandlers.onPlay();
          handleVideoInteraction(playerRef, VideoPlayerState.Playing, video);
        }
        break;
      case VideoPlayerState.Paused:
        eventHandlers.onPause();
        handleVideoInteraction(playerRef, VideoPlayerState.Paused, video);
        break;
      case VideoPlayerState.Ended:
        eventHandlers.onEnded();
        handleVideoInteraction(playerRef, VideoPlayerState.Ended, video);
        break;
    }
  }, [
    playerState,
    playerId,
    playInBackground,
    eventHandlers,
    playerRef,
    handleVideoInteraction,
    video,
    notifyPlayerStarted,
  ]);

  // Effects for initialization and autoplay
  useEffect(() => {
    if (autoPlay && videoUrl && playerRef && !hasStarted) {
      // Add a small delay to ensure the player is fully ready
      const timer = setTimeout(() => {
        setPlayerState(VideoPlayerState.Playing);
        setHasStarted(true);
      }, VIDEO_PLAYER_CONSTANTS.DEFAULT_AUTOPLAY_DELAY);

      return () => clearTimeout(timer);
    }
  }, [autoPlay, videoUrl, playerRef, hasStarted]);

  // Additional effect to handle autoplay state synchronization
  useEffect(() => {
    if (autoPlay && hasStarted && playerState === VideoPlayerState.Playing) {
      // Ensure the player state is properly set when autoplay is active
      setPlayerState(VideoPlayerState.Playing);
    }
  }, [autoPlay, hasStarted, playerState]);

  useEffect(() => {
    if (playInBackground && hasStarted) {
      setPlayerState(VideoPlayerState.Playing);
    }
  }, [playInBackground, hasStarted]);

  // Optimized computed values and state objects
  const playerProvider = useMemo(() => getPlayerProvider(videoUrl || ''), [videoUrl]);
  const effectiveMuted = useMemo(() => getEffectiveMuted(muted, autoPlay), [muted, autoPlay]);
  const isPlaying = useMemo(() => playerState === VideoPlayerState.Playing, [playerState]);

  // Pre-warm the lazy mount before the click handler runs. Wired to
  // pointerdown/focus so ReactPlayer is mounted by the time .play() is
  // invoked synchronously inside handlePlayClick — required for iOS Safari
  // to honor the user gesture for unmuted playback.
  const requestPlayerMount = useCallback(() => {
    setHasRequestedPlayerMount(true);
  }, []);

  // User-initiated play: call .play() synchronously to preserve user gesture context
  // (browsers block programmatic unmuted playback outside direct user interactions)
  const handlePlayClick = useCallback(() => {
    requestPlayerMount();
    pendingPlayRef.current = true;
    const player = playerInstanceRef.current;
    if (player) {
      const api = 'api' in player ? (player as Record<string, unknown>).api : null;
      if (api && typeof (api as Record<string, unknown>).playVideo === 'function') {
        (api as { playVideo: () => void }).playVideo();
      } else if ('play' in player && typeof player.play === 'function') {
        player.play().catch(() => {});
      }
    }
    setPlayerState(VideoPlayerState.Playing);
  }, [requestPlayerMount]);

  // User-initiated pause: clears pending play since user explicitly paused
  const handlePauseClick = useCallback(() => {
    pendingPlayRef.current = false;
    const player = playerInstanceRef.current;
    if (player) {
      const ytApi = 'api' in player ? (player as Record<string, unknown>).api : null;
      if (ytApi && typeof (ytApi as Record<string, unknown>).pauseVideo === 'function') {
        (ytApi as { pauseVideo: () => void }).pauseVideo();
      } else if ('pause' in player && typeof player.pause === 'function') {
        player.pause();
      }
    }
    setPlayerState(VideoPlayerState.Paused);
  }, []);

  // Memoized state objects for children - only recreate when dependencies actually change
  const videoPlayerState = useMemo<VideoPlayerStateObject>(
    () => ({
      playerState,
      testId,
      video,
      isPlaying,
      handlePlayClick,
      handlePauseClick,
      requestPlayerMount,
    }),
    [playerState, testId, video, isPlaying, handlePlayClick, handlePauseClick, requestPlayerMount],
  );

  const setPlayerRef = useCallback((instance: ReactPlayerInstance | null) => {
    playerInstanceRef.current = instance;
    setPlayerRefState(instance);
  }, []);

  // react-player v3's Player.js collects all `on*` props and spreads them
  // onto the underlying DOM element (e.g. <video>).  `onReady` and `onStart`
  // are not valid DOM event handlers and trigger React console warnings.
  //
  // Player.js internally wires:
  //   - `onLoadStart` handler that calls `props.onReady()` then `props.onLoadStart()`
  //   - `onPlay` handler that calls `props.onStart()` (first time) then `props.onPlay()`
  //
  // We route `handleReady` through `onLoadStart` and fold `handleStart` into
  // the `onPlay` handler so neither `onReady` nor `onStart` appear in config.
  const configStartFiredRef = useRef(false);

  const combinedPlayHandler = useCallback(() => {
    if (!configStartFiredRef.current) {
      configStartFiredRef.current = true;
      handleStart();
    }
    handlePlay();
  }, [handleStart, handlePlay]);

  // ReactPlayer configuration - memoized to prevent unnecessary re-renders
  const playerConfig = useMemo(() => {
    if (!videoUrl) {
      return null;
    }

    const config = createReactPlayerConfig({
      src: videoUrl,
      playing: isPlaying,
      loop,
      muted: effectiveMuted,
      controls: !hideNativeControls,
      className: mergedClassNames.player,
      testId: `${testId}-player`,
      onLoadStart: handleReady,
      onPlay: combinedPlayHandler,
      onPause: handlePause,
      onEnded: handleEnded,
      ref: setPlayerRef,
    });

    return config;
  }, [
    videoUrl,
    isPlaying,
    loop,
    effectiveMuted,
    hideNativeControls,
    mergedClassNames.player,
    testId,
    handleReady,
    combinedPlayHandler,
    handlePause,
    handleEnded,
    setPlayerRef,
  ]);

  // Memoize the wrapper props to prevent unnecessary re-renders
  const wrapperProps = useMemo(
    () => ({
      'data-testid': `${testId}-wrapper`,
      className: mergedClassNames.wrapper,
      'data-video-player-state': playerState,
      'data-video-player-provider': playerProvider,
    }),
    [testId, mergedClassNames.wrapper, playerState, playerProvider],
  );

  // Memoize the viewport props
  const viewportProps = useMemo(
    () => ({
      'data-testid': `${testId}-viewport`,
      className: 'relative w-full h-full',
    }),
    [testId],
  );

  const shouldMountPlayer = !lazyLoad || hasRequestedPlayerMount;

  return {
    setPlayerRef,
    playerId,
    requestPlayerMount,
    // Optimized computed values and state objects
    playerProvider,
    effectiveMuted,
    isPlaying,
    videoPlayerState,
    playerConfig,
    wrapperProps,
    viewportProps,
    shouldMountPlayer,
  };
};
